import {gsap} from "gsap/all";
import $ from "jquery";

/**
 * Gauge
 * Animates a horizontal level gauge's fill via clip-path width,
 * keeping the "+" icon pinned to the edge of the fill, and positions
 * a "target" marker at a given percentage along the bar (hidden if 0).
 *
 * fullWidth defaults to whatever "width" is already baked into the
 * SVG's .gauge-clip rect, so it auto-adapts to any gauge size without
 * needing a hardcoded constant per SVG variant.
 *
 * Usage:
 *   const gauge = new Gauge(el);
 *   gauge.setLevel(45);            // jump straight to 45%, no animation
 *   gauge.animateToLevel(100);     // tween to 100%
 *   gauge.setTarget(10);           // place target marker at 10% (0 = hidden)
 */
export class Gauge {

  constructor(container, fullWidth = null, iconOffset = 0) {
    const $svg = $(container).find("svg").first();

    this.clip = $svg.find(".gauge-clip");
    this.icon = $svg.find(".gauge-icon");
    this.target = $svg.find(".gauge-target, .gauge-achieved, .gauge-not-achieved");

    this.container = $(container);
    this.fullWidth = fullWidth ?? parseFloat(this.clip.first().attr("width"));
    this.iconOffset = iconOffset;
    this.width = 0;
    this.targetX = null;

    // Target marker's in my svg at 254.5 (see aptitudes-gauge.svg):
    this.targetNativeCenterX = 254.5;
    // static transform in the SVG centers it vertically: translate(0, -28.1572), it already in the svg
    this.targetOffsetY = -28.1572;

    this.setLevel(0);

    this.currentTimeline = gsap.timeline();
  }

  // pct: 0 to 100
  setLevel(pct) {
    this.width = this.fullWidth * (pct / 100);
    this.apply(this.width);
  }

  // pct: 0 to 100 - animated
  animateToLevel(pct, duration = 0.4) {
    this.currentTimeline.to(this, {
      width: this.fullWidth * (pct / 100),
      duration,
      ease: "power2.out",
      onUpdate: () => this.apply(this.width)
    });
  }

  // pct: 0 to 100 — position the target marker
  // A falsy/zero value hides it entirely.
  setTarget(pct) {
    if (!pct || pct <= 0) {
      this.target.attr("visibility", "hidden");
      this.targetX = null;
      this.updateTargetReached(this.width);
      return;
    }

    this.target.attr("visibility", "visible");

    this.targetX = this.fullWidth * (pct / 100);
    const dx = this.targetX - this.targetNativeCenterX;

    this.target.attr("transform", `translate(${dx}, ${this.targetOffsetY})`);
    this.updateTargetReached(this.width);
  }

  apply(width) {
    this.clip.attr("width", width);
    this.icon.attr("transform", `translate(${width - this.fullWidth + this.iconOffset}, 0)`);

    const pct = (width / this.fullWidth) * 100;
    this.icon.attr("visibility", pct < 15 ? "hidden" : "visible");

    this.updateTargetReached(width);
  }

  updateTargetReached(width) {
    const $aptitude = this.container.closest('.aptitude');

    if (this.targetX == null) {
      $aptitude.removeClass("is-achieved not-achieved");
      return;
    }

    const achieved = width >= this.targetX;
    $aptitude.toggleClass("is-achieved", achieved);
    $aptitude.toggleClass("not-achieved", !achieved);
  }

  reset() {
    this.currentTimeline.kill();
    //reset timeline
    this.currentTimeline = gsap.timeline();

    this.targetX = null;
    this.setLevel(0);
  }
}
